import { configSdkAds } from "@pub/config";
import type { AdtagEnv } from "@sphtech/web2-ads";
import { DISABLE_ADS } from "@src/app/constants";

const PUBLICATION: string | undefined =
  import.meta.env.VITE_PUBLICATION || undefined;

/**
 * Publications cut over from the legacy SPHMGAM engine (src/js/gam.js) to the
 * @sphtech/web2-ads SDK (WEBCR-1557) opt in via `configSdkAds` in their
 * `src/pubs/<pub>/config.ts`. Titles with `enabled: false` keep the legacy
 * engine untouched. `@pub/config` resolves to one publication per build, so a
 * literal `enabled` lets the bundler fold this to `false` and drop the SDK
 * from the other titles' bundles.
 */
export const SDK_ADS_ENABLED = configSdkAds.enabled && !DISABLE_ADS;

/** SDK registry lookup key — magazine titles are namespaced `mag_<pub>`. */
export const SDK_ADS_SITE_KEY = PUBLICATION ? `mag_${PUBLICATION}` : "";

/**
 * Optional adtag-script overrides passed to `AdsProvider`. Magazine defaults
 * are owned by the SDK registry, so publication config normally leaves these
 * unset.
 */
export const SDK_ADS_PREBID_URL = configSdkAds.prebidUrl;
export const SDK_ADS_CONFIG_URL = configSdkAds.configUrl;
export const SDK_ADS_RUNTIME_URL = configSdkAds.runtimeUrl;

const PUBLIC_BASE_URL: string | undefined = import.meta.env
  .VITE_PUBLIC_BASE_URL;

/**
 * Base-URL-derived adtag environment (web2-dev/web2-stg hosts → dev/stg).
 * Parses the hostname via `new URL()` so substrings in the path/query can't
 * accidentally route to the wrong adtag env.
 * Falls back to `dev` under the Vite dev server (`import.meta.env.DEV`) so a
 * local build with no/unknown base URL never loads prod adtag/GPT config; only
 * a real deployed build (DEV=false) with an unrecognised host lands on `prod`.
 */
function resolveAdtagEnv(baseUrl?: string): AdtagEnv {
  if (baseUrl) {
    try {
      const { hostname } = new URL(baseUrl);
      if (hostname.includes("web2-dev")) return "dev";
      if (hostname.includes("web2-stg")) return "stg";
    } catch {
      // Invalid URL — fall through to DEV fallback.
    }
  }
  return import.meta.env.DEV ? "dev" : "prod";
}

export const SDK_ADS_ENV: AdtagEnv = resolveAdtagEnv(PUBLIC_BASE_URL);
